import { IFrameManager } from './iframe/IFrameManager.js';
import { WorkerManager } from './worker/WorkerManager.js';
import { InteractionManager } from './interaction/InteractionManager.js';
import { AudioSystem } from './audio/AudioSystem.js';
import { VideoCoordinator } from './video/VideoCoordinator.js';
import { 
  VijiCoreConfig, 
  VijiCoreError, 
  PerformanceStats, 
  ParameterGroup, 
  FrameRateMode,
  ParameterValue,
  ParameterDefinitionData,
  ParameterUpdateData,
  CoreCapabilities,
  ParameterCategory,
  CaptureFrameOptions
} from './types/index.js';

/**
 * Main VijiCore class - Universal execution engine for creative scenes.
 * 
 * Orchestrates secure IFrame + WebWorker execution with controlled communication.
 * Provides the foundation for scene execution across platform and SDK contexts.
 */
export class VijiCore {
  private iframeManager: IFrameManager | null = null;
  private workerManager: WorkerManager | null = null;
  private interactionManager: InteractionManager | null = null;
  private audioSystem: AudioSystem | null = null;
  private videoCoordinator: VideoCoordinator | null = null;
  private isInitialized = false;
  private isDestroyed = false;
  private isInitializing = false;
  private instanceId: string;
  private screenRefreshRate = 60; // Will be detected
  private debugMode = false; // Debug logging control

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }
  
  // Configuration
  private config: VijiCoreConfig & { 
    frameRateMode: FrameRateMode;
    autoOptimize: boolean;
    parameters: ParameterGroup[];
    noInputs: boolean;
    allowUserInteraction: boolean;
  };

  // Audio stream management
  private currentAudioStream: MediaStream | null = null;

  // Video stream management
  private currentVideoStream: MediaStream | null = null;

  // Interaction state management
  private currentInteractionEnabled: boolean;

  // Parameter system for Phase 2
  private parameterGroups = new Map<string, ParameterGroup>();
  private parameterValues = new Map<string, ParameterValue>();
  private parametersInitialized = false;
  
  // Event listeners for parameter system
  private parameterListeners = new Map<string, Set<(value: ParameterValue) => void>>();
  private parameterDefinedListeners = new Set<(groups: ParameterGroup[]) => void>();
  private parameterErrorListeners = new Set<(error: { message: string; code: string }) => void>();
  private capabilitiesChangeListeners = new Set<(capabilities: CoreCapabilities) => void>();
  
  // Performance tracking (basic for Phase 1)
  private stats: PerformanceStats = {
    frameTime: 0,
    resolution: { width: 0, height: 0 },
    scale: 1.0,
    frameRate: {
      mode: 'full',
      screenRefreshRate: 60,
      effectiveRefreshRate: 60
    },
    parameterCount: 0
  };

  constructor(config: VijiCoreConfig) {
    // Validate required configuration
    this.validateConfig(config);
    
    // Generate unique instance ID for debugging
    this.instanceId = `viji-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Set default configuration values
    this.config = {
      ...config,
      frameRateMode: config.frameRateMode || 'full',
      autoOptimize: config.autoOptimize ?? true,
      parameters: config.parameters || [],
      noInputs: config.noInputs ?? false,
      allowUserInteraction: config.allowUserInteraction ?? true
    };
    
    // Initialize interaction state from config
    this.currentInteractionEnabled = this.config.allowUserInteraction;
    
    this.debugLog(`VijiCore instance created: ${this.instanceId}`);
  }

  /**
   * Capture current scene frame as an image Blob.
   * Resolution can be a scale (0-1+) or explicit width/height with center-crop.
   */
  async captureFrame(options: CaptureFrameOptions = {}): Promise<Blob> {
    if (!this.workerManager || !this.workerManager.ready) {
      throw new VijiCoreError('Core not initialized', 'NOT_INITIALIZED');
    }

    const response = await this.workerManager.sendMessage('capture-frame', {
      type: options.type,
      resolution: options.resolution
    }, 10000);

    // response contains { mimeType, buffer, width, height }
    const buffer: ArrayBuffer = response.buffer;
    const mimeType: string = response.mimeType || options.type || 'image/jpeg';
    return new Blob([buffer], { type: mimeType });
  }

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    // Propagate debug mode to child components when they exist
    if (this.iframeManager && 'setDebugMode' in this.iframeManager) {
      (this.iframeManager as any).setDebugMode(enabled);
    }
    if (this.workerManager && 'setDebugMode' in this.workerManager) {
      (this.workerManager as any).setDebugMode(enabled);
    }
    if (this.interactionManager && 'setDebugMode' in this.interactionManager) {
      (this.interactionManager as any).setDebugMode(enabled);
    }
    if (this.audioSystem && 'setDebugMode' in this.audioSystem) {
      (this.audioSystem as any).setDebugMode(enabled);
    }
    if (this.videoCoordinator && 'setDebugMode' in this.videoCoordinator) {
      (this.videoCoordinator as any).setDebugMode(enabled);
    }
    
    // Also propagate to worker runtime if available
    if (this.workerManager) {
      this.workerManager.postMessage('debug-mode', { enabled });
    }
  }

  /**
   * Get current debug mode status
   */
  getDebugMode(): boolean {
    return this.debugMode;
  }

  /**
   * Initializes the core components in sequence
   */
  async initialize(): Promise<void> {
    try {
      if (this.isDestroyed) {
        throw new VijiCoreError('Cannot initialize destroyed instance', 'INSTANCE_DESTROYED');
      }

      if (this.isInitializing) {
        throw new VijiCoreError('Initialization already in progress', 'CONCURRENT_INITIALIZATION');
      }

      if (this.isInitialized) {
        throw new VijiCoreError('Core already initialized', 'ALREADY_INITIALIZED');
      }

      this.isInitializing = true;
      this.debugLog(`Starting VijiCore initialization... (${this.instanceId})`);

      // Clear any existing content in the container
      this.config.hostContainer.innerHTML = '';

      // 1. Create secure IFrame
      this.iframeManager = new IFrameManager(
        this.config.hostContainer,
      );
      
      await this.iframeManager.createSecureIFrame();
      
      // 2. Create canvas inside IFrame with retry logic
      const offscreenCanvas = await this.createCanvasWithRetry();
      
      // 3. Phase 7: Always setup interaction system for runtime control
      // DOM event listeners are still conditionally created based on enabled state
      this.interactionManager = new InteractionManager();
      this.setupInteractionSystem();
      
      // 4. Create and initialize WebWorker
      this.workerManager = new WorkerManager(
        this.config.sceneCode,
        offscreenCanvas
      );
      
      // 5. Setup communication handlers BEFORE creating worker
      this.setupCommunication();
      
      await this.workerManager.createWorker();
      
      // 6. Initialize AudioSystem (host-side) with callback to send results to worker
      this.audioSystem = new AudioSystem((message) => {
        if (this.workerManager) {
          this.workerManager.postMessage(message.type, message.data);
        }
      });

      // 7. Initialize VideoCoordinator (host-side) with callback to send results to worker
      this.videoCoordinator = new VideoCoordinator((message, transfer) => {
        if (this.workerManager) {
          if (transfer && transfer.length > 0) {
            this.workerManager.postMessage(message.type, message.data, transfer);
          } else {
            this.workerManager.postMessage(message.type, message.data);
          }
        }
      });
      
      // 5. Send initial resolution info to worker
      const effectiveResolution = this.iframeManager.getEffectiveResolution();
      this.workerManager.postMessage('resolution-update', {
        effectiveWidth: effectiveResolution.width,
        effectiveHeight: effectiveResolution.height
      });
      
      // 6. Detect screen refresh rate and update stats
      await this.detectScreenRefreshRate();
      
      // Send detected refresh rate to worker
      this.workerManager.postMessage('refresh-rate-update', {
        screenRefreshRate: this.screenRefreshRate
      });
      
      // 8. Phase 5: Setup audio stream if provided
      if (this.config.audioStream) {
        await this.setAudioStream(this.config.audioStream);
      }
      
      // 9. Phase 10: Setup video stream if provided
      if (this.config.videoStream) {
        await this.setVideoStream(this.config.videoStream);
      }
      
      this.stats.resolution = effectiveResolution;
      this.stats.scale = this.iframeManager.getScale();
      this.updateFrameRateStats();
      
      this.isInitialized = true;
      this.isInitializing = false;
      
      this.debugLog(`VijiCore initialized successfully (${this.instanceId}):`, {
        resolution: `${effectiveResolution.width}x${effectiveResolution.height}`,
        frameRateMode: this.config.frameRateMode,
        hasAudio: !!this.config.audioStream,
        hasVideo: !!this.config.videoStream
      });
    } catch (error) {
      // Clean up on initialization failure
      this.isInitializing = false;
      await this.cleanup();
      
      throw new VijiCoreError(
        `Failed to initialize VijiCore: ${error}`,
        'INITIALIZATION_ERROR',
        { error, config: this.config }
      );
    }
  }

  /**
   * Creates canvas with retry logic to handle timing issues
   */
  private async createCanvasWithRetry(): Promise<OffscreenCanvas> {
    const maxRetries = 3; // Reduced retries since we have better error handling
    const retryDelay = 200;
    
    for (let i = 0; i < maxRetries; i++) {
      // Check if we should continue (not destroyed/destroyed during retry)
      if (this.isDestroyed || !this.isInitializing) {
        throw new VijiCoreError('Initialization cancelled', 'INITIALIZATION_CANCELLED');
      }
      
      if (!this.iframeManager) {
        throw new VijiCoreError('IFrameManager not available', 'MANAGER_NOT_READY');
      }
      
      try {
        return await this.iframeManager.createCanvas();
      } catch (error) {
        console.warn(`Canvas creation attempt ${i + 1}/${maxRetries} failed:`, error);
        
        if (i === maxRetries - 1) {
          // Last attempt failed
          throw error;
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    throw new VijiCoreError('Canvas creation failed after all retries', 'CANVAS_CREATION_TIMEOUT');
  }

  /**
   * Sets up the interaction system for Phase 7
   */
  private setupInteractionSystem(): void {
    if (!this.iframeManager || !this.interactionManager) return;

    // Set up event forwarding from iframe to interaction manager
    this.iframeManager.onInteractionEvent('mouse-update', (data) => {
      this.interactionManager?.updateMouse(data);
      
      // Forward to worker if available
      if (this.workerManager) {
        this.workerManager.postMessage('mouse-update', data);
      }
    });

    this.iframeManager.onInteractionEvent('keyboard-update', (data) => {
      this.interactionManager?.updateKeyboard(data);
      
      // Forward to worker if available
      if (this.workerManager) {
        this.workerManager.postMessage('keyboard-update', data);
      }
    });

    this.iframeManager.onInteractionEvent('touch-update', (data) => {
      this.interactionManager?.updateTouch(data);
      
      // Forward to worker if available
      if (this.workerManager) {
        this.workerManager.postMessage('touch-update', data);
      }
    });

    // Enable interaction events on the iframe based on current state
    this.iframeManager.setInteractionEnabled(this.currentInteractionEnabled);
  }

  /**
   * Sets up communication between components
   */
  private setupCommunication(): void {
    if (!this.workerManager) return;

    // Handle worker messages
    this.workerManager.onMessage('ready', (data) => {
      this.debugLog('Worker ready:', data);
    });

    this.workerManager.onMessage('error', (data) => {
      console.error('Worker error:', data);
    });

    this.workerManager.onMessage('performance-warning', (data) => {
      console.warn('Performance warning:', data);
      // TODO: Handle performance optimization in Phase 8
    });

    this.workerManager.onMessage('performance-update', (data) => {
      // Update stats with real-time performance data from worker
      if (data.effectiveRefreshRate !== undefined) {
        this.stats.frameRate.effectiveRefreshRate = data.effectiveRefreshRate;
        this.stats.frameRate.mode = data.frameRateMode;
        this.stats.frameRate.screenRefreshRate = data.screenRefreshRate;
      }
      if (data.parameterCount !== undefined) {
        this.stats.parameterCount = data.parameterCount;
      }
    });

    // Parameter system message handlers for Phase 2
    this.workerManager.onMessage('parameters-defined', (data: ParameterDefinitionData) => {
      this.handleParametersDefined(data);
    });

    this.workerManager.onMessage('parameter-validation-error', (data) => {
      this.handleParameterError(data);
    });
  }

  // Parameter system implementation for Phase 2

  /**
   * Handle parameter definitions received from worker
   */
  private handleParametersDefined(data: ParameterDefinitionData): void {
    try {
      // Store parameter groups
      this.parameterGroups.clear();
      this.parameterValues.clear();

      for (const group of data.groups) {
        this.parameterGroups.set(group.groupName, group);
        
        // Initialize parameter values with defaults
        for (const [paramName, paramDef] of Object.entries(group.parameters)) {
          this.parameterValues.set(paramName, paramDef.defaultValue);
        }
      }

      this.parametersInitialized = true;
      
      this.debugLog(`Parameters initialized: ${this.parameterValues.size} parameters in ${this.parameterGroups.size} groups`);

      // Send initial parameter values to worker
      this.syncAllParametersToWorker();

      // Notify listeners that parameters are defined
      for (const listener of this.parameterDefinedListeners) {
        try {
          listener(Array.from(this.parameterGroups.values()));
        } catch (error) {
          console.error('Error in parameter defined listener:', error);
        }
      }
    } catch (error) {
      console.error('Error handling parameters defined:', error);
      this.handleParameterError({
        message: `Failed to process parameter definitions: ${(error as Error).message}`,
        code: 'PARAMETER_PROCESSING_ERROR'
      });
    }
  }

  /**
   * Handle parameter validation errors
   */
  private handleParameterError(error: { message: string; code: string }): void {
    console.error('Parameter error:', error);
    
    // Notify error listeners
    for (const listener of this.parameterErrorListeners) {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error in parameter error listener:', listenerError);
      }
    }
  }

  /**
   * Set a single parameter value
   */
  async setParameter(name: string, value: ParameterValue): Promise<void> {
    this.validateReady();
    
    if (!this.parametersInitialized) {
      throw new VijiCoreError('Parameters not yet initialized', 'PARAMETERS_NOT_INITIALIZED');
    }

    if (!this.parameterValues.has(name)) {
      throw new VijiCoreError(`Unknown parameter: ${name}`, 'UNKNOWN_PARAMETER');
    }

    // Update local value
    const oldValue = this.parameterValues.get(name);
    this.parameterValues.set(name, value);

    // Send to worker
    if (this.workerManager) {
      this.workerManager.postMessage('parameter-update', {
        name,
        value
      });
    }

    // Notify listeners if value changed
    if (oldValue !== value) {
      this.notifyParameterListeners(name, value);
    }
  }

  /**
   * Set multiple parameter values efficiently
   */
  async setParameters(values: Record<string, ParameterValue>): Promise<void> {
    this.validateReady();
    
    if (!this.parametersInitialized) {
      throw new VijiCoreError('Parameters not yet initialized', 'PARAMETERS_NOT_INITIALIZED');
    }

    const updates: ParameterUpdateData[] = [];
    const changedParams: Array<{ name: string; value: ParameterValue }> = [];

    // Prepare updates
    for (const [name, value] of Object.entries(values)) {
      if (!this.parameterValues.has(name)) {
        console.warn(`Unknown parameter: ${name}`);
        continue;
      }

      const oldValue = this.parameterValues.get(name);
      this.parameterValues.set(name, value);
      
      updates.push({
        name,
        value,
        timestamp: performance.now()
      });

      if (oldValue !== value) {
        changedParams.push({ name, value });
      }
    }

    // Send batch update to worker
    if (updates.length > 0 && this.workerManager) {
      this.workerManager.postMessage('parameter-batch-update', {
        updates,
        timestamp: performance.now()
      });
    }

    // Notify listeners for changed parameters
    for (const { name, value } of changedParams) {
      this.notifyParameterListeners(name, value);
    }
  }

  /**
   * Get current parameter value
   */
  getParameter(name: string): ParameterValue | undefined {
    return this.parameterValues.get(name);
  }

  /**
   * Get all current parameter values
   */
  getParameterValues(): Record<string, ParameterValue> {
    const values: Record<string, ParameterValue> = {};
    for (const [name, value] of this.parameterValues) {
      values[name] = value;
    }
    return values;
  }

  /**
   * Get parameter groups (for UI generation)
   */
  getParameterGroups(): ParameterGroup[] {
    return Array.from(this.parameterGroups.values());
  }

  /**
   * Get current core capabilities (what's currently active)
   */
  getCapabilities(): CoreCapabilities {
    return {
      hasAudio: this.currentAudioStream !== null,
      hasVideo: this.currentVideoStream !== null,
      hasInteraction: this.currentInteractionEnabled,
      hasGeneral: true // General parameters are always available
    };
  }

  /**
   * Get parameter groups filtered by active capabilities
   */
  getVisibleParameterGroups(): ParameterGroup[] {
    const capabilities = this.getCapabilities();
    const allGroups = this.getParameterGroups();
    
    return allGroups.filter(group => {
      // Filter based on group category
      switch (group.category) {
        case 'audio':
          return capabilities.hasAudio;
        case 'video':
          return capabilities.hasVideo;
        case 'interaction':
          return capabilities.hasInteraction;
        case 'general':
        default:
          return capabilities.hasGeneral;
      }
    }).map(group => ({
      ...group,
      // Also filter individual parameters within groups
      parameters: Object.fromEntries(
        Object.entries(group.parameters).filter(([_, paramDef]) => {
          switch (paramDef.category) {
            case 'audio':
              return capabilities.hasAudio;
            case 'video':
              return capabilities.hasVideo;
            case 'interaction':
              return capabilities.hasInteraction;
            case 'general':
            default:
              return capabilities.hasGeneral;
          }
        })
      )
    }));
  }

  /**
   * Check if a specific parameter category is currently active
   */
  isCategoryActive(category: ParameterCategory): boolean {
    const capabilities = this.getCapabilities();
    switch (category) {
      case 'audio':
        return capabilities.hasAudio;
      case 'video':
        return capabilities.hasVideo;
      case 'interaction':
        return capabilities.hasInteraction;
      case 'general':
      default:
        return capabilities.hasGeneral;
    }
  }

  /**
   * Check if parameters have been initialized
   */
  get parametersReady(): boolean {
    return this.parametersInitialized;
  }

  /**
   * Send all current parameter values to worker (used for initial sync)
   */
  private syncAllParametersToWorker(): void {
    if (!this.workerManager || this.parameterValues.size === 0) {
      return;
    }

    const updates: ParameterUpdateData[] = [];
    for (const [name, value] of this.parameterValues) {
      updates.push({
        name,
        value,
        timestamp: performance.now()
      });
    }

    this.workerManager.postMessage('parameter-batch-update', {
      updates,
      timestamp: performance.now()
    });

    this.debugLog(`Synced ${updates.length} parameter values to worker`);
  }

  /**
   * Add listener for when parameters are defined
   */
  onParametersDefined(listener: (groups: ParameterGroup[]) => void): void {
    this.parameterDefinedListeners.add(listener);
  }

  /**
   * Remove parameter defined listener
   */
  offParametersDefined(listener: (groups: ParameterGroup[]) => void): void {
    this.parameterDefinedListeners.delete(listener);
  }

  /**
   * Add listener for parameter value changes
   */
  onParameterChange(parameterName: string, listener: (value: ParameterValue) => void): void {
    if (!this.parameterListeners.has(parameterName)) {
      this.parameterListeners.set(parameterName, new Set());
    }
    this.parameterListeners.get(parameterName)!.add(listener);
  }

  /**
   * Remove parameter change listener
   */
  offParameterChange(parameterName: string, listener: (value: ParameterValue) => void): void {
    const listeners = this.parameterListeners.get(parameterName);
    if (listeners) {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.parameterListeners.delete(parameterName);
      }
    }
  }

  /**
   * Add listener for parameter errors
   */
  onParameterError(listener: (error: { message: string; code: string }) => void): void {
    this.parameterErrorListeners.add(listener);
  }

  /**
   * Add listener for when core capabilities change (audio/video/interaction state)
   */
  onCapabilitiesChange(listener: (capabilities: CoreCapabilities) => void): void {
    this.capabilitiesChangeListeners.add(listener);
  }

  /**
   * Remove capabilities change listener
   */
  removeCapabilitiesListener(listener: (capabilities: CoreCapabilities) => void): void {
    this.capabilitiesChangeListeners.delete(listener);
  }

  /**
   * Notify capability change listeners
   */
  private notifyCapabilitiesChange(): void {
    const capabilities = this.getCapabilities();
    for (const listener of this.capabilitiesChangeListeners) {
      try {
        listener(capabilities);
      } catch (error) {
        console.error('Error in capabilities change listener:', error);
      }
    }
  }

  /**
   * Remove parameter error listener
   */
  offParameterError(listener: (error: { message: string; code: string }) => void): void {
    this.parameterErrorListeners.delete(listener);
  }

  /**
   * Notify parameter change listeners
   */
  private notifyParameterListeners(name: string, value: ParameterValue): void {
    const listeners = this.parameterListeners.get(name);
    if (listeners) {
      for (const listener of listeners) {
        try {
          listener(value);
        } catch (error) {
          console.error(`Error in parameter listener for '${name}':`, error);
        }
      }
    }
  }

  /**
   * Sets the frame rate to full speed (every animation frame)
   */
  async setFullFrameRate(): Promise<void> {
    this.validateReady();
    
    this.config.frameRateMode = 'full';
    
    // Send frame rate update to worker
    if (this.workerManager) {
      this.workerManager.postMessage('frame-rate-update', { mode: 'full' });
    }
    
    // Update stats
    this.updateFrameRateStats();
    
    this.debugLog(`Frame rate set to full (${this.instanceId})`);
  }

  /**
   * Sets the frame rate to half speed (every second animation frame)
   */
  async setHalfFrameRate(): Promise<void> {
    this.validateReady();
    
    this.config.frameRateMode = 'half';
    
    // Send frame rate update to worker
    if (this.workerManager) {
      this.workerManager.postMessage('frame-rate-update', { mode: 'half' });
    }
    
    // Update stats
    this.updateFrameRateStats();
    
    this.debugLog(`Frame rate set to half (${this.instanceId})`);
  }

  /**
   * Updates the canvas resolution by sending effective dimensions to the worker
   */
  updateResolution() {
    this.validateReady();
    
    if (!this.iframeManager || !this.workerManager) {
      throw new VijiCoreError('Managers not available', 'MANAGERS_NOT_READY');
    }

    const effectiveResolution = this.iframeManager.getEffectiveResolution();
    this.workerManager.postMessage('resolution-update', {
      effectiveWidth: effectiveResolution.width,
      effectiveHeight: effectiveResolution.height
    });

    // Update stats
    this.stats.resolution = effectiveResolution;
    this.stats.scale = this.iframeManager.getScale();
    
    this.debugLog(`Resolution updated successfully (${this.instanceId})`, effectiveResolution);
  }

  /**
   * Sets the audio stream for analysis
   */
  async setAudioStream(audioStream: MediaStream | null): Promise<void> {
    // Don't validate ready during initialization
    if (this.isInitialized && !this.isInitializing) {
      this.validateReady();
    }
    
    // Store current stream and track capability change
    const previouslyHadAudio = this.currentAudioStream !== null;
    this.currentAudioStream = audioStream;
    const nowHasAudio = this.currentAudioStream !== null;
    
    // Process audio on host side using AudioSystem
    if (this.audioSystem) {
      this.audioSystem.handleAudioStreamUpdate({
        audioStream: audioStream,
        ...(this.config.analysisConfig && { analysisConfig: this.config.analysisConfig }),
        timestamp: performance.now()
      });
    }
    
    // Notify capabilities change if audio state changed
    if (previouslyHadAudio !== nowHasAudio) {
      this.notifyCapabilitiesChange();
    }
    
    this.debugLog(`Audio stream ${audioStream ? 'connected' : 'disconnected'} (${this.instanceId})`);
  }

  /**
   * Sets the video stream for processing
   */
  async setVideoStream(videoStream: MediaStream | null): Promise<void> {
    // Don't validate ready during initialization
    if (this.isInitialized && !this.isInitializing) {
      this.validateReady();
    }
    
    // Store current stream and track capability change
    const previouslyHadVideo = this.currentVideoStream !== null;
    this.currentVideoStream = videoStream;
    const nowHasVideo = this.currentVideoStream !== null;
    
    // Process video on host side using VideoCoordinator
    if (this.videoCoordinator) {
      this.videoCoordinator.handleVideoStreamUpdate({
        videoStream: videoStream,
        targetFrameRate: 30, // Default target FPS
        timestamp: performance.now()
      });
    }
    
    // Notify capabilities change if video state changed
    if (previouslyHadVideo !== nowHasVideo) {
      this.notifyCapabilitiesChange();
    }
    
    this.debugLog(`Video stream ${videoStream ? 'connected' : 'disconnected'} (${this.instanceId})`);
  }

  /**
   * Gets the current audio stream
   */
  getAudioStream(): MediaStream | null {
    return this.currentAudioStream;
  }

  /**
   * Gets the current video stream
   */
  getVideoStream(): MediaStream | null {
    return this.currentVideoStream;
  }

  /**
   * Enables or disables user interactions (mouse, keyboard, touch) at runtime
   */
  async setInteractionEnabled(enabled: boolean): Promise<void> {
    // Don't validate ready during initialization
    if (this.isInitialized && !this.isInitializing) {
      this.validateReady();
    }
    
    // Track capability change
    const previouslyHadInteraction = this.currentInteractionEnabled;
    this.currentInteractionEnabled = enabled;
    
    // Enable/disable interaction capture in IFrameManager
    if (this.iframeManager) {
      this.iframeManager.setInteractionEnabled(enabled);
    }
    
    // Notify worker about interaction state change
    if (this.workerManager) {
      this.workerManager.postMessage('interaction-enabled', { enabled });
    }
    
    // Notify capabilities change if interaction state changed
    if (previouslyHadInteraction !== enabled) {
      this.notifyCapabilitiesChange();
    }
    
    this.debugLog(`Interaction ${enabled ? 'enabled' : 'disabled'} (${this.instanceId})`);
  }

  /**
   * Gets the current interaction enabled state
   */
  getInteractionEnabled(): boolean {
    return this.currentInteractionEnabled;
  }

  /**
   * Updates audio analysis configuration
   */
  async setAudioAnalysisConfig(config: { fftSize?: number; smoothing?: number }): Promise<void> {
    this.validateReady();
    
    // Update config
    this.config.analysisConfig = { ...this.config.analysisConfig, ...config };
    
    // Update config in host-side AudioSystem
    if (this.audioSystem) {
      this.audioSystem.handleAudioStreamUpdate({
        audioStream: this.currentAudioStream,
        ...(this.config.analysisConfig && { analysisConfig: this.config.analysisConfig }),
        timestamp: performance.now()
      });
    }
    
    this.debugLog(`Audio analysis config updated (${this.instanceId})`, config);
  }

  /**
   * Updates the canvas resolution by scale
   */
  setResolution(scale: number) {
    this.validateReady();
    
    if (!this.iframeManager || !this.workerManager) {
      throw new VijiCoreError('Managers not available', 'MANAGERS_NOT_READY');
    }
    
    this.debugLog(`Updating resolution scale to:`, scale, `(${this.instanceId})`);
    
    // Update resolution setting
    const effectiveResolution = this.iframeManager.updateScale(scale);
    
    // Send resolution update to worker
    this.workerManager.postMessage('resolution-update', {
      effectiveWidth: effectiveResolution.width,
      effectiveHeight: effectiveResolution.height
    });
    
    // Update stats
    this.stats.resolution = effectiveResolution;
    this.stats.scale = scale;
    
    this.debugLog(`Resolution updated successfully (${this.instanceId})`, effectiveResolution);
  }

  /**
   * Detects the screen refresh rate
   */
  private async detectScreenRefreshRate(): Promise<void> {
    return new Promise((resolve) => {
      let frameCount = 0;
      let startTime = performance.now();
      
      const measureFrames = () => {
        frameCount++;
        if (frameCount === 60) { // Measure for 60 frames
          const elapsed = performance.now() - startTime;
          this.screenRefreshRate = Math.round(60000 / elapsed); // Convert to FPS
          this.debugLog('Detected screen refresh rate:', this.screenRefreshRate + 'Hz');
          resolve();
        } else if (frameCount < 60) {
          requestAnimationFrame(measureFrames);
        }
      };
      
      requestAnimationFrame(measureFrames);
    });
  }

  /**
   * Updates frame rate statistics
   */
  private updateFrameRateStats(): void {
    this.stats.frameRate = {
      mode: this.config.frameRateMode,
      screenRefreshRate: this.screenRefreshRate,
      effectiveRefreshRate: 0 // Will be updated by worker during execution
    };
  }

  /**
   * Gets current performance statistics
   */
  getStats(): PerformanceStats {
    this.validateReady();
    
    if (!this.iframeManager) {
      throw new VijiCoreError('IFrame manager not available', 'MANAGER_NOT_READY');
    }
    
    return this.stats;
  }

  /**
   * Checks if the core is ready for use
   */
  get ready(): boolean {
    return this.isInitialized && 
           !this.isDestroyed && 
           this.iframeManager?.ready === true && 
           this.workerManager?.ready === true;
  }

  /**
   * Gets the current configuration
   */
  get configuration(): Readonly<VijiCoreConfig> {
    return { ...this.config };
  }

  /**
   * Destroys the core instance and cleans up all resources
   */
  async destroy(): Promise<void> {
    if (this.isDestroyed) {
      return; // Already destroyed
    }
    
    this.isDestroyed = true;
    
    // Clear parameter system
    this.parameterGroups.clear();
    this.parameterValues.clear();
    this.parameterListeners.clear();
    this.parameterDefinedListeners.clear();
    this.parameterErrorListeners.clear();
    this.capabilitiesChangeListeners.clear();
    
    // Clean up audio system
    if (this.audioSystem) {
      this.audioSystem.resetAudioState();
      this.audioSystem = null;
    }
    this.currentAudioStream = null;
    
    // Clean up video coordinator
    if (this.videoCoordinator) {
      this.videoCoordinator.resetVideoState();
      this.videoCoordinator = null;
    }
    this.currentVideoStream = null;
    
    await this.cleanup();
    
    this.debugLog(`VijiCore destroyed (${this.instanceId})`);
  }

  /**
   * Validates that the core is ready for operations
   */
  private validateReady(): void {
    if (this.isDestroyed) {
      throw new VijiCoreError('Core instance has been destroyed', 'INSTANCE_DESTROYED');
    }
    
    if (!this.ready) {
      throw new VijiCoreError('Core is not ready', 'CORE_NOT_READY');
    }
  }

  /**
   * Validates the provided configuration
   */
  private validateConfig(config: VijiCoreConfig): void {
    if (!config.hostContainer) {
      throw new VijiCoreError('hostContainer is required', 'INVALID_CONFIG');
    }
    
    if (!config.sceneCode || typeof config.sceneCode !== 'string') {
      throw new VijiCoreError('sceneCode must be a non-empty string', 'INVALID_CONFIG');
    }
    
    if (config.frameRateMode !== undefined && config.frameRateMode !== 'full' && config.frameRateMode !== 'half') {
      throw new VijiCoreError('frameRateMode must be either "full" or "half"', 'INVALID_CONFIG');
    }
  }

  /**
   * Cleans up all resources
   */
  private async cleanup(): Promise<void> {
    try {
      // Destroy worker first to stop rendering
      if (this.workerManager) {
        this.workerManager.destroy();
        this.workerManager = null;
      }
      
      // Destroy IFrame
      if (this.iframeManager) {
        this.iframeManager.destroy();
        this.iframeManager = null;
      }
      
      this.isInitialized = false;
      this.isInitializing = false;
    } catch (error) {
      console.warn('Error during cleanup:', error);
    }
  }
} 